import { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Input, Label, Spinner, Badge, Alert } from 'reactstrap';
import { toast } from 'react-toastify';
import { Btn } from '../../../AbstractElements';
import LegalConsultationService from '../../../Api/services/legal-consultation.service';
import UserService from '../../../Api/services/user.service';
import { CheckCircle, XCircle, Clock } from 'react-feather';
import { useTranslation } from 'react-i18next';
import type { components } from '../../../Api/types/openapi';

type UserSimpleResponse = components['schemas']['UserSimpleResponse'];
type UserMoreBasicResponse = components['schemas']['app__users__serializer__user__UserMoreBasicResponse'];
type LegalConsulationResponse = components['schemas']['LegalConsulationResponse'];

interface AssignLawyerModalProps {
  isOpen: boolean;
  toggle: () => void;
  consultationId: number;
  currentLawyerId?: number | null;
  consultationDate?: string | null;
  consultationStartTime?: string | null;
  consultationEndTime?: string | null;
  onSuccess: () => void;
}

interface LawyerAvailability {
  lawyer: UserSimpleResponse;
  available: boolean;
  checking: boolean;
}

const AssignLawyerModal = ({ 
  isOpen, 
  toggle, 
  consultationId, 
  currentLawyerId, 
  consultationDate,
  consultationStartTime,
  consultationEndTime,
  onSuccess 
}: AssignLawyerModalProps) => {
  const { t } = useTranslation();
  const [lawyers, setLawyers] = useState<UserSimpleResponse[]>([]);
  const [lawyersAvailability, setLawyersAvailability] = useState<LawyerAvailability[]>([]);
  const [selectedLawyerId, setSelectedLawyerId] = useState<number | null>(currentLawyerId || null);
  const [loading, setLoading] = useState(false);
  const [loadingLawyers, setLoadingLawyers] = useState(true);
  const [consultation, setConsultation] = useState<LegalConsulationResponse | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadConsultation();
      loadLawyers();
      setSelectedLawyerId(currentLawyerId || null);
    }
  }, [isOpen, currentLawyerId, consultationId]);

  const loadConsultation = async () => {
    try {
      const data = await LegalConsultationService.getConsultationById(consultationId);
      setConsultation(data);
    } catch (error: any) {
      console.error('Error loading consultation:', error);
    }
  };

  const loadLawyers = async () => {
    try {
      setLoadingLawyers(true);
      
      if (process.env.NODE_ENV === 'development') {
        console.log('📥 AssignLawyerModal - Cargando abogados disponibles...');
        console.log('   Consultation ID:', consultationId);
      }
      
      let lawyersList: UserSimpleResponse[] = [];
      let useSpecificEndpoint = false;
      
      // PRIORIDAD: Usar el endpoint específico para abogados disponibles de esta consulta
      // GET /api/core/legal-consulations/lawyers/available/{case_id}
      if (consultationId) {
        try {
          if (process.env.NODE_ENV === 'development') {
            console.log('🎯 Usando endpoint específico para abogados disponibles de la consulta');
          }
          
          const availableLawyers = await LegalConsultationService.getAvailableLawyersForCase(consultationId);
          
          // Convertir UserMoreBasicResponse[] a UserSimpleResponse[] (solo campos necesarios)
          lawyersList = availableLawyers.map((lawyer: UserMoreBasicResponse): UserSimpleResponse => ({
            id: lawyer.id,
            name: lawyer.name,
            email: lawyer.email,
            username: null,
            is_superadmin: false,
            active: true,
            role: null,
            phone: null,
            country_code_phone: null,
            photo_url: lawyer.photo_url,
            country: null,
            created_at: null,
            language: null,
            confirm_email: lawyer.confirm_email,
          }));
          
          useSpecificEndpoint = true; // Marcamos que usamos el endpoint específico
          
          if (process.env.NODE_ENV === 'development') {
            console.log('✅ AssignLawyerModal - Abogados disponibles cargados desde endpoint específico:', {
              count: lawyersList?.length || 0,
              lawyers: lawyersList?.map(l => ({ id: l.id, name: l.name, email: l.email })) || [],
            });
          }
        } catch (error: any) {
          // Si falla el endpoint específico, usar el método anterior como fallback
          console.warn('⚠️ No se pudo obtener abogados disponibles del endpoint específico, usando fallback:', error.message);
          
          if (process.env.NODE_ENV === 'development') {
            console.log('🔄 Fallback: Usando endpoint general de abogados activos');
          }
          
          // FALLBACK: Obtener todos los abogados activos
          const allLawyers = await UserService.getActiveLawyers();
          lawyersList = allLawyers || [];
          useSpecificEndpoint = false;
        }
      } else {
        // Si no hay consultationId, usar el método anterior
        if (process.env.NODE_ENV === 'development') {
          console.log('🔄 No hay consultationId, usando endpoint general de abogados activos');
        }
        
        const allLawyers = await UserService.getActiveLawyers();
        lawyersList = allLawyers || [];
        useSpecificEndpoint = false;
      }
      
      if (!lawyersList || lawyersList.length === 0) {
        toast.warning('No hay abogados disponibles para esta consulta');
      }
      
      setLawyers(lawyersList);
      
      // Si usamos el endpoint específico, todos los abogados ya están filtrados como disponibles
      // Solo necesitamos verificar disponibilidad manualmente si usamos el método anterior
      if (useSpecificEndpoint) {
        // Marcar todos como disponibles (ya vienen filtrados del endpoint específico)
        setLawyersAvailability(
          lawyersList.map(lawyer => ({
            lawyer,
            available: true,
            checking: false,
          }))
        );
      } else {
        // Verificar disponibilidad manualmente (método anterior)
        await checkLawyersAvailability(lawyersList);
      }
    } catch (error: any) {
      console.error('❌ Error loading lawyers:', error);
      console.error('❌ Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        url: error.config?.url,
      });
      const errorMessage = error.response?.data?.detail || error.message || 'Error al cargar lista de abogados';
      toast.error(errorMessage);
    } finally {
      setLoadingLawyers(false);
    }
  };

  const checkLawyersAvailability = async (lawyersList: UserSimpleResponse[]) => {
    // Usar datos de la consulta o props
    const date = consultationDate || consultation?.consultation_date;
    const startTime = consultationStartTime || consultation?.init_consultation_time;
    const endTime = consultationEndTime || consultation?.end_consultation_time;

    if (!date || !startTime || !endTime) {
      // Si no hay fecha/hora, marcar todos como disponibles (sin verificar)
      setLawyersAvailability(
        lawyersList.map(lawyer => ({
          lawyer,
          available: true,
          checking: false,
        }))
      );
      return;
    }

    // Verificar disponibilidad de cada abogado
    const availabilityPromises = lawyersList.map(async (lawyer) => {
      // VALIDAR que el abogado tenga ID
      if (!lawyer.id) {
        console.warn(`Abogado sin ID: ${lawyer.name || lawyer.email || 'Desconocido'}`);
        return {
          lawyer,
          available: false,
          checking: false,
        };
      }
      
      try {
        const isAvailable = await LegalConsultationService.getLawyerAvailability(
          lawyer.id,
          date,
          startTime,
          endTime,
          consultationId
        );
        return {
          lawyer,
          available: isAvailable,
          checking: false,
        };
      } catch (error) {
        console.error(`Error checking availability for lawyer ${lawyer.id}:`, error);
        return {
          lawyer,
          available: false,
          checking: false,
        };
      }
    });

    const results = await Promise.all(availabilityPromises);
    setLawyersAvailability(results);
  };

  const handleAssign = async () => {
    if (!selectedLawyerId) {
      toast.warning('Por favor selecciona un abogado');
      return;
    }

    try {
      setLoading(true);
      await LegalConsultationService.assignToLawyer({
        lawyer: selectedLawyerId,
        legal: consultationId,
      });
      toast.success('Abogado asignado exitosamente');
      onSuccess();
      toggle();
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || error.message || 'Error al asignar abogado';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getAvailabilityBadge = (lawyerAvailability: LawyerAvailability) => {
    if (lawyerAvailability.checking) {
      return (
        <Badge color="secondary" className="d-flex align-items-center">
          <Spinner size="sm" className="me-1" />
          Verificando...
        </Badge>
      );
    }
    
    if (lawyerAvailability.available) {
      return (
        <Badge color="success" className="d-flex align-items-center">
          <CheckCircle size={14} className="me-1" />
          Disponible
        </Badge>
      );
    } else {
      return (
        <Badge color="danger" className="d-flex align-items-center">
          <XCircle size={14} className="me-1" />
          Ocupado
        </Badge>
      );
    }
  };

  const hasDateAndTime = consultationDate && consultationStartTime && consultationEndTime;

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle}>Asignar Abogado</ModalHeader>
      <ModalBody>
        {loadingLawyers ? (
          <div className="text-center py-3">
            <Spinner color="primary" />
            <p className="mt-2">Cargando abogados y verificando disponibilidad...</p>
          </div>
        ) : (
          <>
            {!hasDateAndTime && !consultation?.consultation_date && (
              <Alert color="warning" className="mb-3">
                <Clock size={16} className="me-2" />
                <strong>Nota:</strong> Para verificar disponibilidad, la consulta debe tener fecha y hora asignadas.
              </Alert>
            )}

            {hasDateAndTime && (
              <div className="mb-3 p-2 bg-light rounded">
                <small className="text-muted">
                  <strong>Fecha:</strong> {consultationDate && new Date(consultationDate).toLocaleDateString('es-ES')} |{' '}
                  <strong>Horario:</strong> {consultationStartTime} - {consultationEndTime}
                </small>
              </div>
            )}

            <Label for="lawyer-select">Seleccionar Abogado</Label>
            <Input
              type="select"
              id="lawyer-select"
              value={selectedLawyerId || ''}
              onChange={(e) => setSelectedLawyerId(e.target.value ? parseInt(e.target.value) : null)}
              className={selectedLawyerId && lawyersAvailability.find(la => la.lawyer.id === selectedLawyerId && !la.available) ? 'border-danger' : ''}
            >
              <option value="">-- Selecciona un abogado --</option>
              {/* Primero los disponibles */}
              {hasDateAndTime && (
                <>
                  {lawyersAvailability
                    .filter(la => la.available)
                    .map((lawyerAvailability) => {
                      const lawyer = lawyerAvailability.lawyer;
                      return (
                        <option key={lawyer.id} value={lawyer.id}>
                          ✓ {lawyer.name || lawyer.email} {lawyer.email && lawyer.name ? `(${lawyer.email})` : ''}
                        </option>
                      );
                    })}
                  {/* Separador si hay disponibles y ocupados */}
                  {lawyersAvailability.some(la => la.available) && 
                   lawyersAvailability.some(la => !la.available) && (
                    <option disabled>──────────</option>
                  )}
                  {/* Luego los ocupados */}
                  {lawyersAvailability
                    .filter(la => !la.available)
                    .map((lawyerAvailability) => {
                      const lawyer = lawyerAvailability.lawyer;
                      const isSelected = selectedLawyerId === lawyer.id;
                      return (
                        <option 
                          key={lawyer.id} 
                          value={lawyer.id}
                          disabled={!isSelected}
                        >
                          ✗ {lawyer.name || lawyer.email} {lawyer.email && lawyer.name ? `(${lawyer.email})` : ''} - OCUPADO
                        </option>
                      );
                    })}
                </>
              )}
              {/* Si no hay fecha/hora, mostrar todos sin verificar */}
              {!hasDateAndTime && lawyersAvailability.map((lawyerAvailability) => {
                const lawyer = lawyerAvailability.lawyer;
                return (
                  <option key={lawyer.id} value={lawyer.id}>
                    {lawyer.name || lawyer.email} {lawyer.email && lawyer.name ? `(${lawyer.email})` : ''}
                  </option>
                );
              })}
            </Input>

            {/* Lista de disponibilidad */}
            {hasDateAndTime && lawyersAvailability.length > 0 && (
              <div className="mt-3">
                <Label className="small">Disponibilidad de Abogados:</Label>
                <div className="list-group mt-2" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {/* Primero los disponibles */}
                  {lawyersAvailability
                    .filter(la => la.available)
                    .map((lawyerAvailability) => {
                      const lawyer = lawyerAvailability.lawyer;
                      const isSelected = selectedLawyerId === lawyer.id;
                      
                      return (
                        <div
                          key={lawyer.id}
                          className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center ${
                            isSelected ? 'active' : ''
                          }`}
                          style={{ cursor: 'pointer', borderLeft: '3px solid #28a745' }}
                          onClick={() => setSelectedLawyerId(lawyer.id || null)}
                        >
                          <div>
                            <strong>{lawyer.name || lawyer.email}</strong>
                            {lawyer.email && lawyer.name && (
                              <div className="small text-muted">{lawyer.email}</div>
                            )}
                          </div>
                          {getAvailabilityBadge(lawyerAvailability)}
                        </div>
                      );
                    })}
                  
                  {/* Luego los ocupados */}
                  {lawyersAvailability
                    .filter(la => !la.available)
                    .map((lawyerAvailability) => {
                      const lawyer = lawyerAvailability.lawyer;
                      const isSelected = selectedLawyerId === lawyer.id;
                      
                      return (
                        <div
                          key={lawyer.id}
                          className={`list-group-item list-group-item-action d-flex justify-content-between align-items-center ${
                            isSelected ? 'active' : ''
                          } bg-light`}
                          style={{ 
                            cursor: isSelected ? 'pointer' : 'not-allowed',
                            borderLeft: '3px solid #dc3545',
                            opacity: isSelected ? 1 : 0.7
                          }}
                          onClick={() => {
                            if (isSelected) {
                              setSelectedLawyerId(lawyer.id || null);
                            }
                          }}
                        >
                          <div>
                            <strong>{lawyer.name || lawyer.email}</strong>
                            {lawyer.email && lawyer.name && (
                              <div className="small text-muted">{lawyer.email}</div>
                            )}
                          </div>
                          {getAvailabilityBadge(lawyerAvailability)}
                        </div>
                      );
                    })}
                </div>
              </div>
            )}

            {!loadingLawyers && lawyers.length === 0 && (
              <Alert color="warning" className="mt-3">
                <strong>No hay abogados disponibles</strong>
                <br />
                <small>No se encontraron abogados activos en el sistema. Por favor, contacta al administrador.</small>
              </Alert>
            )}

            {selectedLawyerId && lawyersAvailability.find(la => la.lawyer.id === selectedLawyerId && !la.available) && hasDateAndTime && (
              <Alert color="danger" className="mt-3">
                <strong>Advertencia:</strong> El abogado seleccionado tiene un conflicto de horario en esta fecha y hora.
                ¿Desea continuar de todas formas?
              </Alert>
            )}
          </>
        )}
      </ModalBody>
      <ModalFooter>
        <Btn color="secondary" onClick={toggle} disabled={loading}>
          {t('Cancelar')}
        </Btn>
        <Btn 
          color="primary" 
          onClick={handleAssign} 
          disabled={loading || !selectedLawyerId || loadingLawyers}
        >
          {loading ? (
            <>
              <Spinner size="sm" className="me-2" />
              {t('Asignando...')}
            </>
          ) : (
            t('Aceptar')
          )}
        </Btn>
      </ModalFooter>
    </Modal>
  );
};

export default AssignLawyerModal;

